import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

export type FilterOption = {
  value: string;
  label: string;
  detail?: string;
  prefix?: ReactNode;
};

/** Styled, keyboard-friendly replacement for native selects in the manager UI.
 * Native option popups belong to the browser/OS and cannot share the game's
 * visual language reliably. */
export function FilterSelect({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder = 'Seleccionar',
  className = '',
}: {
  value: string;
  options: FilterOption[];
  onChange(value: string): void;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const listId = useId();
  const selected = options.find((option) => option.value === value);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return <div className={`filter-select ${open ? 'is-open' : ''} ${className}`} ref={root}>
    <button type="button" className="filter-select-trigger" aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} aria-controls={listId} onClick={() => setOpen((current) => !current)}>
      {selected?.prefix && <span className="filter-select-value-icon">{selected.prefix}</span>}
      <span className="filter-select-value"><b>{selected?.label ?? placeholder}</b>{selected?.detail && <small>{selected.detail}</small>}</span>
      <ChevronDown size={15} />
    </button>
    {open && <div id={listId} className="filter-select-menu" role="listbox" aria-label={ariaLabel}>
      {options.map((option) => <button type="button" role="option" aria-selected={option.value === value} className={option.value === value ? 'selected' : ''} onClick={() => { onChange(option.value); setOpen(false); }} key={option.value}>
        {option.prefix && <span className="filter-select-option-icon">{option.prefix}</span>}
        <span><b>{option.label}</b>{option.detail && <small>{option.detail}</small>}</span>
        {option.value === value && <Check size={14} />}
      </button>)}
    </div>}
  </div>;
}
